import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { Icon } from './Icon.jsx';

// v2.4 — Search-only client picker. Mirrors the design prototype's
// `ClientSelect`: no "add new" affordance, no dropdown of every client
// by default — open → focus search input → type → pick. When the
// search yields zero matches, the popover terminates in a muted
// dead-end ("Ask an admin to add it…") rather than offering a create
// row. Calendar / Jira import rows additionally pass `disabled` +
// `disabledLabel` so the trigger can read "Pick activity first" /
// "Not applicable" until the row's activity actually takes a client.
//
// Exported helper `filterClients` is the same case-insensitive
// substring filter used by the popover; the admin directory's table
// uses it too so the two screens never diverge on what "matches"
// means.

export function filterClients(clients, query) {
  const q = String(query || '').trim().toLowerCase();
  const arr = (clients || []).filter(Boolean);
  if (!q) return arr;
  const prefix = [];
  const rest = [];
  for (const c of arr) {
    const lower = String(c).toLowerCase();
    if (lower.startsWith(q)) prefix.push(c);
    else if (lower.includes(q)) rest.push(c);
  }
  return [...prefix, ...rest];
}

export function ClientSelect({
  value,
  onChange,
  clients,
  placeholder = 'Pick a client',
  emptyLabel = 'No client',
  searchPlaceholder = 'Search clients…',
  disabled = false,
  disabledLabel = '',
  error = false,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  // Stable id prefix per instance so the combobox/listbox wiring works
  // even when multiple ClientSelects coexist on the page (the import
  // overlays have one per row).
  const listboxId = useId();
  const optionId = (i) => `${listboxId}-opt-${i}`;

  // Close on outside click.
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  // Focus the search input as soon as the popover mounts.
  useEffect(() => {
    if (!open) return;
    setQuery('');
    setHighlight(0);
    const t = setTimeout(() => inputRef.current?.focus(), 0);
    return () => clearTimeout(t);
  }, [open]);

  useEffect(() => {
    setHighlight(0);
  }, [query]);

  // Keep the active option visible on keyboard navigation. Cheap
  // lookup — the popover is short, and `nearest` avoids the page
  // jumping when an option is already in view.
  useEffect(() => {
    if (!open || !listRef.current) return;
    const el = listRef.current.querySelector(`#${CSS.escape(optionId(highlight))}`);
    el?.scrollIntoView({ block: 'nearest' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlight, open]);

  const filtered = useMemo(
    () => filterClients(clients, query),
    [clients, query],
  );

  const commit = (name) => {
    onChange(name);
    setOpen(false);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlight((h) => Math.min(filtered.length - 1, h + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlight((h) => Math.max(0, h - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // Sole-match shortcut from the design: a single result + Enter
      // picks it, regardless of where the highlight is.
      const pick =
        filtered.length === 1 ? filtered[0] : filtered[highlight];
      if (pick) commit(pick);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setOpen(false);
    }
  };

  const triggerLabel = disabled
    ? disabledLabel || placeholder
    : value || placeholder;
  const triggerMuted = disabled || !value;

  return (
    <div ref={wrapRef} style={{ position: 'relative' }}>
      <button
        type="button"
        className={'tt-input ' + (error ? 'is-error' : '')}
        onClick={() => !disabled && setOpen((o) => !o)}
        disabled={disabled}
        aria-disabled={disabled || undefined}
        title={disabled ? disabledLabel : undefined}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          textAlign: 'left',
          cursor: disabled ? 'not-allowed' : 'pointer',
          opacity: disabled ? 0.55 : 1,
        }}
      >
        <span
          style={{
            color: triggerMuted ? 'var(--sp-fg-3)' : 'var(--sp-fg-1)',
            fontStyle: disabled ? 'italic' : 'normal',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {triggerLabel}
        </span>
        <span
          style={{
            color: 'var(--sp-fg-3)',
            display: 'inline-flex',
            marginLeft: 8,
          }}
        >
          <Icon name="chevD" size={16} />
        </span>
      </button>
      {open && !disabled && (
        <div className="tt-pop" style={{ top: 'calc(100% + 4px)', left: 0 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              borderBottom: '1px solid var(--sp-border, #E9E9E9)',
            }}
          >
            <span style={{ color: 'var(--sp-fg-3)', display: 'inline-flex' }}>
              <Icon name="search" size={14} />
            </span>
            <input
              ref={inputRef}
              type="text"
              // a11y wiring — combobox over a listbox of the filtered
              // rows below. Placeholder alone is not an accessible name.
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-autocomplete="list"
              aria-activedescendant={
                filtered[highlight] ? optionId(highlight) : undefined
              }
              aria-label={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              style={{
                flex: 1,
                border: 0,
                outline: 0,
                background: 'transparent',
                fontSize: 13,
                color: 'var(--sp-fg-1)',
                padding: '2px 0',
              }}
            />
          </div>
          {/* Clear / "No client" row — only shown when search is empty,
              so it doesn't muddle the result list. Suppressed when the
              field is required (caller passes emptyLabel={null}); a
              required client should never be clearable from the picker. */}
          {!query.trim() && emptyLabel && (
            <div
              className="tt-pop-item"
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <span style={{ color: 'var(--sp-fg-3)', fontStyle: 'italic' }}>
                {emptyLabel}
              </span>
            </div>
          )}
          {query.trim() && filtered.length === 0 && (
            // `aria-live` announces the dead-end so SR users hear
            // "No client matches…" instead of an empty popover.
            <div
              className="tt-pop-item"
              role="status"
              aria-live="polite"
              style={{
                color: 'var(--sp-fg-3)',
                cursor: 'default',
                flexDirection: 'column',
                alignItems: 'flex-start',
                padding: '10px 12px',
              }}
            >
              <span style={{ fontWeight: 500, color: 'var(--sp-fg-2)' }}>
                No client matches “{query.trim()}”.
              </span>
              <span style={{ fontSize: 12, marginTop: 2 }}>
                Ask an admin to add it in the Client directory.
              </span>
            </div>
          )}
          <div
            ref={listRef}
            id={listboxId}
            role="listbox"
            aria-label="Clients"
          >
            {filtered.map((name, i) => (
              <div
                key={name}
                id={optionId(i)}
                role="option"
                aria-selected={name === value}
                className={
                  'tt-pop-item ' +
                  (name === value ? 'is-active' : '') +
                  (i === highlight ? ' is-active' : '')
                }
                onMouseEnter={() => setHighlight(i)}
                onClick={() => commit(name)}
              >
                <span>{name}</span>
                {name === value && (
                  <span style={{ marginLeft: 'auto', color: 'var(--sp-fg-1)' }}>
                    <Icon name="check" size={14} />
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
